import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { C1DrawerLink } from 'components/core/drawer_link'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Link } from 'components/core/links/C1Link'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TablePagination } from 'components/core/pagination/C1TablePagination'
import { AppUserDisplayNameWithFetch } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { routes } from 'lib/routes/routes'
import { AlertCircle, IdCard, Lock } from 'lucide-react'
import {
    AccessProfileUnenrollmentBehavior,
    AccessProfileWithExpiry,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementUserBindingRef } from 'pbts/c1/api/app/v1/app_refs'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { accessProfilesMultiGetApi } from 'redux_toolkit/entitlements'

type AccessProfilesColumnProps = {
    appId: string
    appEntitlementId: string
    appUserId: string
}

/**
 * Component that displays the access profiles (catalogs) that power a grant.
 * Shows the first access profile, with a "+N" link to open a drawer for more.
 * Uses multiGet pattern for automatic batching and caching.
 */
export const AccessProfilesColumn: FC<AccessProfilesColumnProps> = ({
    appId,
    appEntitlementId,
    appUserId,
}) => {
    // Memoize the ref to avoid creating new objects on every render
    // This ensures proper batching and cache deduplication
    const bindingRef = useMemo(
        () => AppEntitlementUserBindingRef.create({ appId, appEntitlementId, appUserId }),
        [appId, appEntitlementId, appUserId],
    )

    // Use multiGet for automatic batching and caching across rows
    const { data: userProfiles, isLoading, isError } = accessProfilesMultiGetApi.useMultiGet(bindingRef)

    if (isLoading) {
        return <Skeleton width={100} />
    }

    if (isError) {
        return (
            <Tooltip title="Failed to load access profiles">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'error.main' }}>
                    <AlertCircle size={16} />
                    <Typography variant="body2" color="error">
                        Error
                    </Typography>
                </Stack>
            </Tooltip>
        )
    }

    if (!userProfiles?.accessProfiles?.length) {
        return <C1EmptyIndicator />
    }

    const accessProfiles = userProfiles.accessProfiles
    const firstProfile = accessProfiles[0]

    const drawerContent = (
        <AccessProfilesDrawer
            appId={appId}
            appEntitlementId={appEntitlementId}
            appUserId={appUserId}
            accessProfiles={accessProfiles}
        />
    )

    return (
        <Stack direction="row" alignItems="center" spacing={2}>
            <C1DrawerLink
                variant="md"
                label={
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {firstProfile.displayName || firstProfile.catalogId}
                    </Typography>
                }
                drawerContent={drawerContent}
            />
            {accessProfiles.length > 1 && (
                <C1DrawerLink
                    variant="md"
                    label={
                        <Box sx={{ whiteSpace: 'nowrap' }}>
                            <FormattedMessage
                                defaultMessage="+{count}"
                                id="component_access_profiles_more"
                                values={{ count: accessProfiles.length - 1 }}
                            />
                        </Box>
                    }
                    drawerContent={drawerContent}
                />
            )}
        </Stack>
    )
}

type AccessProfilesTableExtras = Record<string, never>

/**
 * Maps the unenrollment behavior enum to a user-friendly display label.
 */
const getUnenrollmentBehaviorLabel = (behavior: AccessProfileUnenrollmentBehavior): React.ReactElement => {
    switch (behavior) {
        case AccessProfileUnenrollmentBehavior.LEAVE_ACCESS_AS_IS:
            return (
                <FormattedMessage
                    id="access_profiles_unenrollment_leave_as_is"
                    defaultMessage="Leave access as is"
                />
            )
        case AccessProfileUnenrollmentBehavior.REVOKE_ALL:
            return (
                <FormattedMessage
                    id="access_profiles_unenrollment_revoke_all"
                    defaultMessage="Revoke all entitlements"
                />
            )
        case AccessProfileUnenrollmentBehavior.REVOKE_UNJUSTIFIED:
            return (
                <FormattedMessage
                    id="access_profiles_unenrollment_revoke_unjustified"
                    defaultMessage="Revoke unjustified entitlements"
                />
            )
        default:
            return (
                <FormattedMessage
                    id="access_profiles_unenrollment_unspecified"
                    defaultMessage="Unspecified"
                />
            )
    }
}

const cols: C1TableReduxColumn<AccessProfileWithExpiry, AccessProfilesTableExtras>[] = [
    {
        field: 'accessProfile',
        flex: 2,
        label: (
            <FormattedMessage
                id="access_profiles_table_column_name"
                defaultMessage="Access profile"
            />
        ),
        Component: ({ row: profile }): React.ReactElement => (
            <C1Link href={routes.accessProfiles.detail(profile.catalogId)}>
                <Typography
                    sx={{
                        color: 'primary.main',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {profile.displayName || profile.catalogId}
                </Typography>
            </C1Link>
        ),
    },
    {
        field: 'expiration',
        flex: 1,
        label: (
            <FormattedMessage
                id="access_profiles_table_column_expiration"
                defaultMessage="Expiration"
            />
        ),
        Component: ({ row: profile }): React.ReactElement => {
            if (!profile.expiresAt) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage
                            id="access_profiles_table_no_expiry"
                            defaultMessage="No expiration"
                        />
                    </Typography>
                )
            }
            const date = Timestamp.toDate(profile.expiresAt)
            return (
                <Typography variant="body2" color="text.secondary">
                    <FormattedDate
                        value={date}
                        year="numeric"
                        month="short"
                        day="2-digit"
                    />
                </Typography>
            )
        },
    },
    {
        field: 'unenrollmentPolicy',
        flex: 1,
        label: (
            <FormattedMessage
                id="access_profiles_table_column_unenrollment"
                defaultMessage="Unenrollment policy"
            />
        ),
        Component: ({ row: profile }): React.ReactElement => (
            <Typography variant="body2" color="text.secondary">
                {getUnenrollmentBehaviorLabel(profile.unenrollmentBehavior)}
            </Typography>
        ),
    },
]

type AccessProfilesDrawerProps = {
    appId: string
    appEntitlementId: string
    appUserId: string
    accessProfiles: AccessProfileWithExpiry[]
}

const PAGE_SIZE = 10

const AccessProfilesDrawerContent: FC<AccessProfilesDrawerProps> = ({
    appId,
    appEntitlementId,
    appUserId,
    accessProfiles,
}) => {
    const [page, setPage] = useState(0)

    // Split access profiles into pages
    const paginatedData = useMemo(() => {
        const pages: AccessProfileWithExpiry[][] = []
        for (let i = 0; i < accessProfiles.length; i += PAGE_SIZE) {
            pages.push(accessProfiles.slice(i, i + PAGE_SIZE))
        }
        return pages.length > 0 ? pages : [[]]
    }, [accessProfiles])

    const canLoadMore = (page + 1) * PAGE_SIZE < accessProfiles.length

    const onPageChange = useCallback(async (newPage: number): Promise<void> => {
        setPage(newPage)
    }, [])

    const tableExtras: AccessProfilesTableExtras = {}

    const table = (
        <C1TableRedux<AccessProfileWithExpiry, AccessProfilesTableExtras>
            data={paginatedData}
            cols={cols}
            canLoadMore={false}
            isError={false}
            isLoading={false}
            page={page}
            getId={(profile): string => profile.catalogId}
            staticExtraProps={tableExtras}
        />
    )

    return (
        <C1DrawerInner>
            <C1DrawerInner.Header>
                <Stack spacing={1} my={4}>
                    <Typography variant="h2" component="div">
                        <FormattedMessage
                            defaultMessage="Access profiles"
                            id="components_access_profiles_drawer_header"
                        />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage
                            defaultMessage="Access profiles define how long this access remains active."
                            id="components_access_profiles_drawer_description"
                        />
                    </Typography>
                </Stack>
            </C1DrawerInner.Header>
            <C1DrawerInner.Content>
                <Stack spacing={4}>
                    <Stack spacing={2}>
                        <Typography variant="h5">
                            <FormattedMessage
                                id="access_profiles_drawer_details_label"
                                defaultMessage="Details"
                            />
                        </Typography>
                        <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" spacing={3}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '10rem' }}>
                                    <Lock size={24} color="currentColor" style={{ color: 'var(--mui-palette-text-secondary)' }} />
                                    <Typography color="text.secondary">
                                        <FormattedMessage
                                            id="access_profiles_drawer_entitlement_label"
                                            defaultMessage="Entitlement"
                                        />
                                    </Typography>
                                </Stack>
                                <C1EntitlementDisplayNameWithFetch
                                    appId={appId}
                                    entitlementId={appEntitlementId}
                                />
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={3}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '10rem' }}>
                                    <IdCard size={24} color="currentColor" style={{ color: 'var(--mui-palette-text-secondary)' }} />
                                    <Typography color="text.secondary">
                                        <FormattedMessage
                                            id="access_profiles_drawer_account_label"
                                            defaultMessage="Account"
                                        />
                                    </Typography>
                                </Stack>
                                <AppUserDisplayNameWithFetch appId={appId} id={appUserId} />
                            </Stack>
                        </Stack>
                    </Stack>
                    {table}
                </Stack>
            </C1DrawerInner.Content>
            {accessProfiles.length > PAGE_SIZE && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderTopColor: 'border.subtle' }}>
                    <C1TablePagination
                        page={page}
                        pageSize={PAGE_SIZE}
                        data={paginatedData}
                        canLoadMore={canLoadMore}
                        onPageChange={onPageChange}
                        totalCount={accessProfiles.length}
                        sx={{ marginTop: 0 }}
                    />
                </Box>
            )}
        </C1DrawerInner>
    )
}

const AccessProfilesDrawer: FC<AccessProfilesDrawerProps> = (props) => {
    return <AccessProfilesDrawerContent {...props} />
}
